import { HTTPStatusCodes } from '@omnipresentgroup/constants';
import {
  Button,
  Inline,
  notifySuccess,
  notifyWarning,
} from '@omnipresentgroup/design-system';
import {
  useDocumentDelete,
  useDocumentDownload,
} from '@omnipresentgroup/domains';
import { ConfirmationModal } from '@omnipresentgroup/shared';
import { DocumentAction } from '@omnipresentgroup/types';
import { useState } from 'react';

const DOCUMENT_BEING_PROCESSED_MESSAGE =
  'This document is being processed and will be available soon.';
const DOCUMENT_DELETED_MESSAGE = 'Document deleted successfully.';
const MODAL_DESCRIPTION =
  'Deleting this document is permanent and cannot be undone. Are you sure you want to proceed?';

type DocumentActionsCellProps = {
  actions: DocumentAction[];
  id: string;
};

export const DocumentActionsCell = ({
  actions,
  id,
}: DocumentActionsCellProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canDownload = actions.includes(DocumentAction.DOWNLOAD);
  const canView = actions.includes(DocumentAction.DOWNLOAD);
  const canDelete = actions.includes(DocumentAction.DELETE);

  const { downloadDocument } = useDocumentDownload();

  const { mutate: deleteDocument, isPending: isDeleting } = useDocumentDelete({
    onSuccess: () => {
      notifySuccess({
        title: DOCUMENT_DELETED_MESSAGE,
      });
      setIsModalOpen(false);
    },
  });

  const downloadDocumentHandler = (viewMode?: boolean) => {
    const payload = viewMode
      ? {
          documentId: id,
          disposition: 'inline',
        }
      : {
          documentId: id,
        };
    const target = viewMode ? '_blank' : '_self';

    downloadDocument(payload).then(({ documentLink, status }) => {
      if (status === HTTPStatusCodes.ACCEPTED) {
        notifyWarning({
          title: DOCUMENT_BEING_PROCESSED_MESSAGE,
        });
      } else {
        window.open(documentLink, target);
      }
    });
  };

  return (
    <>
      <Inline gap="8" align="center">
        <Button
          icon="Eye"
          variant="tertiary"
          size="small"
          disabled={!canView}
          onClick={() => {
            if (canView) {
              downloadDocumentHandler(true);
            }
          }}
        />
        <Button
          icon="Download"
          variant="tertiary"
          size="small"
          disabled={!canDownload}
          onClick={() => {
            if (canDownload) {
              downloadDocumentHandler();
            }
          }}
        />
        <Button
          icon="Trash"
          variant="tertiary"
          size="small"
          disabled={!canDelete}
          onClick={() => {
            if (canDelete) {
              setIsModalOpen(true);
            }
          }}
        />
      </Inline>
      <ConfirmationModal
        modalProps={{
          open: isModalOpen,
          onOpenChange: setIsModalOpen,
        }}
        handleConfirm={() => deleteDocument(id)}
        handleCancel={() => setIsModalOpen(false)}
        loading={isDeleting}
        description={MODAL_DESCRIPTION}
      />
    </>
  );
};
